import { addMilliseconds, intervalToDuration } from 'date-fns';
import { memoAll } from '../../../CodesForFoundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { Attribute, ObjectManager, component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { AudioManager } from '../../../platform/AudioManager';
import { ServerDate } from '../../../platform/ServerDate';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { FixedRectangular } from '../../canvas/entities/FixedRectangular';
import { Physical, PotentialInteraction, TransformHints } from '../../canvas/entities/Physical';
import { Snappable } from '../../canvas/entities/Snappable';
import { StopwatchMinutesField, StopwatchSecondsField } from './StopwatchFields';
import { StopwatchLayout } from './StopwatchLayout';
import { stopwatchPropertyLayout } from './StopwatchPropertyLayout';

const mutation = defineCommand(
    'stopwatchMutate',
    'Stopwatch Mutate',
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const invocation = defineCommand(
    'stopwatchInteraction',
    'Stopwatch Interaction',
    (r, [id, region]: [string, string?]) => {
        const audio = r.call(AudioManager);
        const objects = r.call(ObjectManager);
        const watch = objects.get(id, Stopwatch);
        objects.update(() => {
            if (region === 'startStop') {
                if (!watch.timeStarted) {
                    // Start
                    watch.timeStarted = ServerDate.now();
                }
                else {
                    // Stop
                    watch.timeStarted = 0;
                    audio.stop(`${watch.id}/alarm`);
                }
            }
            else if (region === 'addTime') {
                watch.minutes = ((parseInt(watch.minutes, 10) || 0) + 1).toString();
            }
        });
    },
    [],
);

const stopwatchDef = {
    name: 'Stopwatch',
    implements: [Physical, Snappable],
    mutation,
    invocation,
    propertyLayout: stopwatchPropertyLayout,
};

export const StopwatchReadingAttr = new Attribute<[string, string], Stopwatch>('reading', null);

@component(stopwatchDef)
export class Stopwatch extends FixedRectangular implements Snappable {

    @property({ type: 'number', default: 280 })
    public width!: number;

    @property({ type: 'number', default: 140 })
    public height!: number;

    @property({ type: 'string', default: '#3f51b5' })
    public color!: string;

    @property({ type: 'string', oldName: 'minutes|value' })
    public minutes!: string;

    @property({ type: 'string', oldName: 'seconds|value' })
    public seconds!: string;

    @property({ type: 'number' })
    public timeStarted!: number;

    @memoUsing('minutes', 'seconds')
    public get timeBank(): number {
        const m = (parseInt(this.minutes) || 0);
        const s = (parseInt(this.seconds) || 0);
        // Total milliseconds:
        return (s + (m * 60)) * 1000;
    }

    public get boundary() {
        return true;
    }

    public get shouldSnap() {
        return true;
    }

    @memoUsing('width', 'height')
    public get layout() {
        return StopwatchLayout(this);
    }

    @memoUsing('layout', 'minutes', 'seconds')
    public get uiFields() {
        return [
            StopwatchMinutesField(this),
            StopwatchSecondsField(this),
        ];
    };

    public getTimeRemaining() {

        const now = ServerDate.now();
        const remaining = this.timeBank - (now - this.timeStarted);

        const start = new Date(this.timeStarted);
        const end = addMilliseconds(start, remaining);

        if (start < end) {
            const { hours, minutes, seconds } = intervalToDuration({ start, end });
            const m = (hours * 60) + minutes;
            const s = seconds;
            return [m, s] as const;
        }
        else {
            return [0, 0] as const;
        }
    }

    public getTimeRemainingMs() {
        const [m, s] = this.getTimeRemaining();
        return ((m * 60) + s) * 1000;
    }

    public getTimeRemainingFormatted() {
        const values = Array.from(this.getTimeRemaining());
        if (!values[0]) values.splice(0, 1);
        return values.map(x => x.toString().padStart(2, '0')).join(':');
    }

    @memoAll()
    public queryTransformHints(): TransformHints {
        return {
            scaleX: false,
            scaleY: false,
            rotate: false,
            translate: true,
            visible: true,
        };
    }

    public testInteraction({ type, mpt }: PotentialInteraction): [boolean, string] {
        const { startStop, addTime } = this.layout;
        const rpt = this.toRelative(mpt);
        if (this.localBounds.contains(rpt)) {
            if (type === 'invoke') {
                if (startStop.contains(rpt)) return [true, 'startStop'];
                if (addTime.contains(rpt)) return [true, 'addTime'];
            }
            else {
                return [true, 'default'];
            }
        }
        return [false, 'none'];
    }
}
